import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ChevronLeft } from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const from = location.state?.from?.pathname || '/home';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Login realizado com sucesso!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao fazer login'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1518893063132-36e46dbe2428?w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background" />
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-brand text-4xl font-bold text-foreground mb-4">
            Bem-vindo de volta
          </h2>
          <p className="text-muted-foreground text-lg max-w-md">
            Continua a tua jornada pelos melhores contos eróticos.
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-start lg:items-center justify-center p-6 sm:p-8 pt-10 lg:pt-8">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 sm:mb-8 text-muted-foreground hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>

          {/* Logo */}
          <Link to="/" className="block mb-6 sm:mb-8">
            <img src="/logo-red.png" alt="Intimate.pt" className="h-9 sm:h-10 w-auto" />
          </Link>

          <h2 className="text-2xl font-semibold text-foreground mb-2">Entrar</h2>
          <p className="text-muted-foreground mb-8">
            Não tens uma conta?{' '}
            <Link to="/register" className="text-ruby hover:underline">
              Criar conta
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="login-email"
                className="bg-surface border-border h-12"
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Palavra-passe</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-ruby hover:underline"
                  data-testid="forgot-password-link"
                >
                  Esqueceste-te da palavra-passe?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  data-testid="login-password"
                  className="bg-surface border-border h-12 pr-12"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-ruby hover:bg-ruby-light text-white rounded-full py-6 font-semibold"
            >
              {loading ? 'A entrar...' : 'Entrar'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
